import type { useHasPermission } from '../hooks/usePermissionGuard';

/**
 * Whether the viewer may perform a write, and — when they may not — the sentence that
 * says why. `reason` is what a disabled control's tooltip shows.
 *
 * Extracted from `pages/organization/inboxSettings/model.ts` (SKU-1790) in SKU-1798 so
 * the support-inbox composer and the settings page cannot drift into two different
 * answers to "can this person write, and what do we tell them if not". That file
 * re-exports it, so its own callers are unchanged.
 *
 * The three "no" answers are kept apart on purpose. A permission check still in flight,
 * one that FAILED, and one where the server said no are different facts, and telling
 * someone their role lacks access when the lookup merely errored is a false statement
 * about their account (see `computeLookupFailed` in `usePermissionGuard`).
 */
export interface WriteGate {
  allowed: boolean;
  reason: string | null;
}

type PermissionResult = Pick<
  ReturnType<typeof useHasPermission>,
  'hasPermissions' | 'loading' | 'lookupFailed'
>;

export const gateFrom = (result: PermissionResult, permission: string): WriteGate => {
  if (result.loading) {
    return { allowed: false, reason: 'Checking your permissions…' };
  }
  if (result.lookupFailed) {
    return {
      allowed: false,
      reason: `Could not confirm whether you hold ${permission}. Reload the page to try again.`,
    };
  }
  if (!result.hasPermissions) {
    return { allowed: false, reason: `Requires the ${permission} permission.` };
  }
  return { allowed: true, reason: null };
};
